import deepmerge from 'deepmerge';
import React, { createContext, useMemo, useState } from 'react';
import type { DeepPartial } from '~/types';

export type ContextType = {
  currentParams: Record<string, any>;
  currentPath: string;
  currentUrl: string;
  setCurrentUrl: React.Dispatch<React.SetStateAction<string>>;
};

const defaultContext: ContextType = {
  currentParams: {},
  currentPath: '',
  currentUrl: '',
  setCurrentUrl() {}
};

const RouteContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  requestUrl?: string;
}>;

export function RouteContextProvider(props: OwnProps) {
  const [currentUrl, setCurrentUrl] = useState(props.requestUrl ?? '');

  const url = useMemo(() => {
    return new URL(currentUrl, 'https://www.dummy.com');
  }, [currentUrl]);

  const value = useMemo<ContextType>(
    () => ({
      currentParams: Object.fromEntries(url.searchParams.entries()),
      currentPath: url.pathname,
      currentUrl: currentUrl,
      setCurrentUrl
    }),
    [currentUrl, url.pathname, url.searchParams]
  );

  return (
    <RouteContext.Provider value={value}>
      {props.children}
    </RouteContext.Provider>
  );
}

export const generateRouteContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedRouteContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <RouteContext.Provider value={generateRouteContext(mockValue)}>
      {children}
    </RouteContext.Provider>
  );
}

export default RouteContext;
